import { Archive, ArchiveRestore, PackageCheck, Pencil, Settings, ShieldAlert, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { Panel } from '@/components/common/Panel'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useDeleteProject, useSaveProject } from '../../api'
import { PROJECT_WRITE_PERM } from '../../permissions'
import type { Project } from '../../types'
import { toastError } from '../../ui'
import { ProjectFormModal } from '../ProjectFormModal'
import { ProjectInfoPanel } from '../ProjectInfoPanel'
import { ReleasesPanel } from '../ReleasesPanel'
import { TeamPanel } from '../TeamPanel'

export function SettingsTab({ project }: { project: Project }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const navigate = useNavigate()
  const save = useSaveProject()
  const remove = useDeleteProject()
  const [editing, setEditing] = useState(false)
  const canWrite = can(PROJECT_WRITE_PERM)
  const archived = project.status === 'archived'

  const toggleArchive = async () => {
    const ok = await confirmModal({
      title: t(archived ? 'projects.settingsTab.restoreTitle' : 'projects.settingsTab.archiveTitle'),
      description: t(archived ? 'projects.settingsTab.restoreBody' : 'projects.settingsTab.archiveBody', { name: project.name }),
      confirmLabel: t(archived ? 'projects.settingsTab.restore' : 'projects.settingsTab.archive'),
    })
    if (!ok) return
    const { key, name, description, icon, color, kind, sectorId, startDate, endDate } = project
    save.mutate(
      { id: project.id, input: { key, name, description, icon, color, kind, sectorId, startDate, endDate, status: archived ? 'active' : 'archived' } },
      { onSuccess: () => toast.success(t('projects.updated')), onError: toastError },
    )
  }

  const onDelete = async () => {
    const ok = await confirmModal({
      title: t('projects.deleteTitle'),
      description: t('projects.deleteBody', { name: project.name, members: project.team.length, items: project.tasks.length }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(project.id)
      toast.success(t('projects.deleted'))
      navigate('/projects/boards', { replace: true })
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="space-y-4">
      <Panel
        icon={Settings}
        title={t('projects.settingsTab.general')}
        hint={t('projects.settingsTab.generalHint', { key: project.key })}
        bodyClassName="p-0 sm:p-0"
        actions={
          canWrite ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil />
              {t('common.edit')}
            </Button>
          ) : null
        }
      >
        <ProjectInfoPanel project={project} />
      </Panel>
      <Panel icon={PackageCheck} title={t('projects.releases.title')} hint={t('projects.settingsTab.releasesHint')} bodyClassName="p-0 sm:p-0">
        <ReleasesPanel project={project} />
      </Panel>
      </div>

      <div className="space-y-4">
        <section className="rounded-2xl border bg-card shadow-card">
          <TeamPanel project={project} />
        </section>

        {canWrite ? (
          <Panel icon={ShieldAlert} tone="orange" title={t('projects.settingsTab.danger')} hint={t('projects.settingsTab.dangerHint')}>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{t(archived ? 'projects.settingsTab.restore' : 'projects.settingsTab.archive')}</p>
                  <p className="text-xs text-muted-foreground">{t(archived ? 'projects.settingsTab.restoreHint' : 'projects.settingsTab.archiveHint')}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => void toggleArchive()} disabled={save.isPending}>
                  {archived ? <ArchiveRestore /> : <Archive />}
                  {t(archived ? 'projects.settingsTab.restore' : 'projects.settingsTab.archive')}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/[0.04] p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-destructive">{t('projects.delete')}</p>
                  <p className="text-xs text-muted-foreground">{t('projects.settingsTab.deleteHint')}</p>
                </div>
                <Button variant="destructive" size="sm" onClick={() => void onDelete()} disabled={remove.isPending}>
                  <Trash2 />
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          </Panel>
        ) : null}
      </div>

      <ProjectFormModal open={editing} project={project} onOpenChange={setEditing} />
    </div>
  )
}
